import { Fragment, useEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp, LockKeyhole, LogIn, MessageCircle, Search, Send, ShieldCheck, Smile, Tag, Users, X } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { ChatContact, ChatGroupSummary, ChatMessage } from '../types';
import { formatTime } from '../utils';
import { Badge, Button, EmptyBlock, LoadingBlock, PageHeader, Textarea } from '../components/ui';
import { UserTitleBadge } from '../components/UserTitleBadge';

type ChatTab = 'group' | 'private' | 'oversight';
type OversightType = 'all' | 'private' | 'group';

function mergeMessages(current: ChatMessage[] | null, incoming: ChatMessage[]) {
  const seen = new Set((current || []).map(message => message.id));
  return [...(current || []), ...incoming.filter(message => !seen.has(message.id))];
}

function formatChatListTime(timestamp: number | null | undefined) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return new Intl.DateTimeFormat('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  }
  if (date.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat('zh-CN', { month: '2-digit', day: '2-digit' }).format(date);
  }
  return new Intl.DateTimeFormat('zh-CN', { year: '2-digit', month: '2-digit', day: '2-digit' }).format(date);
}

function formatDayLabel(timestamp: number) {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return '今天';
  if (date.toDateString() === yesterday.toDateString()) return '昨天';
  return new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
}

function sameDay(a: number, b: number) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function Avatar({ name, avatar, small = false }: { name: string; avatar?: string | null; small?: boolean }) {
  return <span className={'chat-avatar' + (small ? ' small' : '')}>{avatar ? <img className="avatar-image" src={avatar} alt="" /> : name.slice(0, 1).toUpperCase()}</span>;
}

const QUICK_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣',
  '😊', '😇', '🙂', '😉', '😍', '🥰', '😘', '😋',
  '😜', '🤪', '🤔', '🤨', '😐', '😑', '😴', '😷',
  '🥳', '🥺', '😢', '😭', '😤', '😠', '😡', '🤯',
  '😳', '😱', '🤗', '🤝', '👍', '👎', '👏', '🙏',
  '💪', '❤️', '🧡', '💛', '💚', '💙', '💜', '🎉',
  '🔥', '✨', '🌹', '🍀', '☕', '🍰', '🐱', '🐶'
];

function EmojiPicker({ onPick, onClose }: { onPick: (emoji: string) => void; onClose: () => void }) {
  return (
    <section className="chat-emoji-panel" aria-label="选择表情">
      <header><strong>常用表情</strong><button type="button" onClick={onClose} aria-label="关闭表情面板"><X size={18} /></button></header>
      <div className="chat-emoji-grid">
        {QUICK_EMOJIS.map(emoji => <button type="button" key={emoji} onClick={() => onPick(emoji)} aria-label={`插入表情 ${emoji}`}>{emoji}</button>)}
      </div>
    </section>
  );
}

function ChatComposer({
  draft,
  onDraftChange,
  onSubmit,
  placeholder,
  busy,
  emojiOpen,
  onToggleEmoji,
  onCloseEmoji,
  onPickEmoji,
  inputRef,
  onFocus,
  onBlur
}: {
  draft: string;
  onDraftChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  placeholder: string;
  busy: boolean;
  emojiOpen: boolean;
  onToggleEmoji: () => void;
  onCloseEmoji: () => void;
  onPickEmoji: (emoji: string) => void;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  onFocus: () => void;
  onBlur: () => void;
}) {
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.style.height = '0px';
    input.style.height = `${Math.min(Math.max(input.scrollHeight, 44), 96)}px`;
  }, [draft, inputRef]);

  return (
    <form className="chat-composer" onSubmit={onSubmit}>
      {emojiOpen && <EmojiPicker onPick={onPickEmoji} onClose={onCloseEmoji} />}
      <div className="chat-composer-box">
        <button type="button" className={'chat-emoji-trigger' + (emojiOpen ? ' active' : '')} onMouseDown={event => event.preventDefault()} onClick={onToggleEmoji} aria-label={emojiOpen ? '关闭表情面板' : '打开表情面板'} aria-expanded={emojiOpen}>
          <Smile size={20} />
        </button>
        <Textarea ref={inputRef} value={draft} onChange={event => onDraftChange(event.target.value)} onFocus={onFocus} onBlur={onBlur} rows={1} maxLength={1000} enterKeyHint="send" placeholder={placeholder} required />
        <Button className="chat-send-button" variant="primary" type="submit" loading={busy} onMouseDown={event => event.preventDefault()}><Send size={16} /><span>发送</span></Button>
      </div>
    </form>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  return (
    <div className={'chat-message' + (message.mine ? ' mine' : '')}>
      {!message.mine && <Avatar name={message.senderUsername} avatar={message.senderAvatar} />}
      <div className="chat-message-stack">
        {message.channelType === 'group'
          ? <div className="chat-message-identity"><strong className="chat-message-sender">{message.senderDisplayName}</strong><UserTitleBadge role={message.senderRole} customTitle={message.senderCustomTitle} compact /></div>
          : !message.mine && <strong className="chat-message-sender">{message.senderDisplayName}</strong>}
        <div className="chat-message-content"><p>{message.content}</p></div>
        <time className="chat-message-time">{formatChatListTime(message.createdAt)}</time>
      </div>
      {message.mine && <Avatar name={message.senderUsername} avatar={message.senderAvatar} />}
    </div>
  );
}

function MessageFeed({ messages, emptyTitle, emptyDescription, scrollRef, onScroll }: {
  messages: ChatMessage[] | null;
  emptyTitle: string;
  emptyDescription: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  onScroll?: () => void;
}) {
  return (
    <div className="chat-message-scroll" ref={scrollRef} onScroll={onScroll}>
      {!messages ? <div className="chat-message-silent-loading" aria-hidden="true" /> : messages.length === 0 ? <EmptyBlock title={emptyTitle} description={emptyDescription} /> : (
        <div className="chat-message-list">
          {messages.map((message, index) => (
            <Fragment key={message.id}>
              {(index === 0 || !messages[index - 1] || !sameDay(messages[index - 1]!.createdAt, message.createdAt)) && <div className="chat-date-divider"><span>{formatDayLabel(message.createdAt)}</span></div>}
              <MessageBubble message={message} />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function scrollFeedToBottom(ref: RefObject<HTMLDivElement | null>) {
  window.requestAnimationFrame(() => {
    const target = ref.current;
    if (target) target.scrollTop = target.scrollHeight;
  });
}

export function ChatPage({ onNeedAuth }: { onNeedAuth: () => void }) {
  const { user, permissions } = useAuth();
  const { toast } = useUi();
  const [tab, setTab] = useState<ChatTab>('group');
  const [mobileConversationOpen, setMobileConversationOpen] = useState(false);
  const [groupMessages, setGroupMessages] = useState<ChatMessage[] | null>(null);
  const [groupSummary, setGroupSummary] = useState<ChatGroupSummary | null>(null);
  const [groupDraft, setGroupDraft] = useState('');
  const [groupBusy, setGroupBusy] = useState(false);
  const [contacts, setContacts] = useState<ChatContact[] | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [contactsOpen, setContactsOpen] = useState(false);
  const [selectedPeer, setSelectedPeer] = useState<ChatContact | null>(null);
  const [privateMessages, setPrivateMessages] = useState<ChatMessage[] | null>(null);
  const [privateDraft, setPrivateDraft] = useState('');
  const [privateBusy, setPrivateBusy] = useState(false);
  const [oversightType, setOversightType] = useState<OversightType>('all');
  const [oversightMessages, setOversightMessages] = useState<ChatMessage[] | null>(null);
  const [isMobileLayout, setIsMobileLayout] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [composerFocused, setComposerFocused] = useState(false);
  const groupLastSeq = useRef(0);
  const privateLastSeq = useRef(0);
  const groupScroll = useRef<HTMLDivElement | null>(null);
  const privateScroll = useRef<HTMLDivElement | null>(null);
  const groupInputRef = useRef<HTMLTextAreaElement | null>(null);
  const privateInputRef = useRef<HTMLTextAreaElement | null>(null);
  const groupAtBottom = useRef(true);
  const privateAtBottom = useRef(true);
  const allowed = Boolean(user && (user.boundToGame || permissions.isSuperadmin));

  const closeMobileConversation = () => {
    setEmojiOpen(false);
    setComposerFocused(false);
    if (window.history.state?.lspdChatDetail) window.history.back();
    else setMobileConversationOpen(false);
  };

  const loadGroup = async (afterSeq = 0) => {
    try {
      const data = await api<{ messages: ChatMessage[]; lastSeq: number }>(`/api/chat/group/messages?afterSeq=${afterSeq}&limit=80`);
      setGroupMessages(current => afterSeq > 0 ? (data.messages.length > 0 ? mergeMessages(current, data.messages) : current) : data.messages);
      groupLastSeq.current = data.lastSeq;
      const latest = data.messages.length > 0 ? data.messages[data.messages.length - 1] : null;
      setGroupSummary(current => ({
        unreadCount: 0,
        lastMessage: latest?.content ?? current?.lastMessage ?? '',
        lastMessageAt: latest?.createdAt ?? current?.lastMessageAt ?? null
      }));
      window.dispatchEvent(new Event('lspd:chat-unread-updated'));
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '群聊加载失败', 'error');
    }
  };

  const loadContacts = async () => {
    try {
      const data = await api<{ contacts: ChatContact[]; group: ChatGroupSummary }>('/api/chat/contacts');
      setContacts(data.contacts);
      setGroupSummary(data.group);
      window.dispatchEvent(new Event('lspd:chat-unread-updated'));
      setSelectedPeer(current => {
        if (!current) return data.contacts[0] || null;
        return data.contacts.find(contact => contact.id === current.id) || current;
      });
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '联系人加载失败', 'error');
    }
  };

  const loadPrivate = async (peerId: string, afterSeq = 0) => {
    try {
      const data = await api<{ messages: ChatMessage[]; lastSeq: number; peer: ChatContact }>(`/api/chat/private/${peerId}/messages?afterSeq=${afterSeq}&limit=80`);
      setPrivateMessages(current => afterSeq > 0 ? (data.messages.length > 0 ? mergeMessages(current, data.messages) : current) : data.messages);
      privateLastSeq.current = data.lastSeq;
      setContacts(current => current?.map(contact => contact.id === peerId ? { ...contact, unreadCount: 0 } : contact) || current);
      window.dispatchEvent(new Event('lspd:chat-unread-updated'));
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '私聊加载失败', 'error');
    }
  };

  const loadOversight = async () => {
    if (!permissions.isSuperadmin) return;
    try {
      const data = await api<{ messages: ChatMessage[] }>(`/api/admin/chat/messages?type=${oversightType}&limit=200`);
      setOversightMessages(data.messages);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '监管消息加载失败', 'error');
    }
  };

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px)');
    const update = () => setIsMobileLayout(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!allowed) return;
    void loadContacts();
    const timer = window.setInterval(() => void loadContacts(), 15_000);
    return () => window.clearInterval(timer);
  }, [allowed]);

  useEffect(() => {
    if (!allowed || tab !== 'group' || (isMobileLayout && !mobileConversationOpen)) return;
    setGroupMessages(null);
    groupLastSeq.current = 0;
    groupAtBottom.current = true;
    void loadGroup(0);
    const timer = window.setInterval(() => void loadGroup(groupLastSeq.current), 5000);
    return () => window.clearInterval(timer);
  }, [allowed, tab, isMobileLayout, mobileConversationOpen]);

  useEffect(() => {
    if (!allowed || tab !== 'private' || !selectedPeer || (isMobileLayout && !mobileConversationOpen)) return;
    setPrivateMessages(null);
    privateLastSeq.current = 0;
    privateAtBottom.current = true;
    void loadPrivate(selectedPeer.id, 0);
    const timer = window.setInterval(() => void loadPrivate(selectedPeer.id, privateLastSeq.current), 5000);
    return () => window.clearInterval(timer);
  }, [allowed, tab, selectedPeer?.id, isMobileLayout, mobileConversationOpen]);

  useEffect(() => {
    if (!allowed || tab !== 'oversight' || !permissions.isSuperadmin || (isMobileLayout && !mobileConversationOpen)) return;
    setOversightMessages(null);
    void loadOversight();
    const timer = window.setInterval(() => void loadOversight(), 5000);
    return () => window.clearInterval(timer);
  }, [allowed, tab, permissions.isSuperadmin, oversightType, isMobileLayout, mobileConversationOpen]);

  useEffect(() => {
    setEmojiOpen(false);
    setComposerFocused(false);
  }, [tab, selectedPeer?.id, mobileConversationOpen]);

  useEffect(() => {
    if (!isMobileLayout || !mobileConversationOpen || !window.matchMedia('(orientation: portrait)').matches) return;
    if (!window.history.state?.lspdChatDetail) {
      window.history.pushState({ ...(window.history.state || {}), lspdChatDetail: true }, '', window.location.href);
    }
    const onPopState = () => {
      if (!window.history.state?.lspdChatDetail) setMobileConversationOpen(false);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isMobileLayout, mobileConversationOpen, tab, selectedPeer?.id]);

  useEffect(() => {
    const onHardwareBack = (event: Event) => {
      const detail = (event as CustomEvent<{ canGoBack: boolean; handled: boolean }>).detail;
      if (!detail || !isMobileLayout || !mobileConversationOpen || !window.matchMedia('(orientation: portrait)').matches) return;
      detail.handled = true;
      closeMobileConversation();
    };
    window.addEventListener('lspd:hardware-back', onHardwareBack);
    return () => window.removeEventListener('lspd:hardware-back', onHardwareBack);
  }, [isMobileLayout, mobileConversationOpen, tab, selectedPeer?.id]);

  useEffect(() => {
    if (isMobileLayout && !mobileConversationOpen) return;
    if (tab === 'group') {
      groupAtBottom.current = true;
      scrollFeedToBottom(groupScroll);
    } else if (tab === 'private') {
      privateAtBottom.current = true;
      scrollFeedToBottom(privateScroll);
    }
  }, [tab, selectedPeer?.id, mobileConversationOpen, isMobileLayout]);

  useEffect(() => {
    if (groupAtBottom.current) scrollFeedToBottom(groupScroll);
  }, [groupMessages]);

  useEffect(() => {
    if (privateAtBottom.current) scrollFeedToBottom(privateScroll);
  }, [privateMessages]);

  useEffect(() => {
    if (!emojiOpen) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.chat-emoji-panel') || target.closest('.chat-emoji-trigger')) return;
      setEmojiOpen(false);
      setComposerFocused(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePress, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePress, true);
  }, [emojiOpen]);

  useEffect(() => {
    const active = isMobileLayout && mobileConversationOpen && window.matchMedia('(orientation: portrait)').matches;
    document.body.classList.toggle('chat-detail-open', active);
    return () => document.body.classList.remove('chat-detail-open');
  }, [isMobileLayout, mobileConversationOpen]);

  useEffect(() => {
    const active = composerFocused || emojiOpen;
    if (!isMobileLayout || !active) {
      document.body.classList.remove('chat-composer-active');
      document.documentElement.style.removeProperty('--chat-visual-height');
      document.documentElement.style.removeProperty('--chat-visual-top');
      return;
    }
    document.body.classList.add('chat-composer-active');
    const syncViewport = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      const top = window.visualViewport?.offsetTop || 0;
      document.documentElement.style.setProperty('--chat-visual-height', `${height}px`);
      document.documentElement.style.setProperty('--chat-visual-top', `${top}px`);
      window.requestAnimationFrame(() => {
        const target = tab === 'private' ? privateScroll.current : groupScroll.current;
        if (target) target.scrollTop = target.scrollHeight;
      });
    };
    syncViewport();
    window.visualViewport?.addEventListener('resize', syncViewport);
    window.addEventListener('resize', syncViewport);
    return () => {
      document.body.classList.remove('chat-composer-active');
      document.documentElement.style.removeProperty('--chat-visual-height');
      document.documentElement.style.removeProperty('--chat-visual-top');
      window.visualViewport?.removeEventListener('resize', syncViewport);
      window.removeEventListener('resize', syncViewport);
    };
  }, [composerFocused, emojiOpen, isMobileLayout, tab, selectedPeer?.id]);
  const sendGroup = async (event: FormEvent) => {
    event.preventDefault();
    const content = groupDraft.trim();
    if (!content) return;
    setGroupBusy(true);
    try {
      const data = await api<{ message: ChatMessage }>('/api/chat/group/messages', { method: 'POST', body: JSON.stringify({ content }) });
      groupAtBottom.current = true;
      setGroupMessages(current => mergeMessages(current, [data.message]));
      groupLastSeq.current = Math.max(groupLastSeq.current, data.message.seq);
      setGroupDraft('');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发送失败', 'error');
    } finally {
      setGroupBusy(false);
    }
  };

  const sendPrivate = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedPeer) return;
    const content = privateDraft.trim();
    if (!content) return;
    setPrivateBusy(true);
    try {
      const data = await api<{ message: ChatMessage }>(`/api/chat/private/${selectedPeer.id}/messages`, { method: 'POST', body: JSON.stringify({ content }) });
      setPrivateMessages(current => mergeMessages(current, [data.message]));
      privateLastSeq.current = Math.max(privateLastSeq.current, data.message.seq);
      setPrivateDraft('');
      void loadContacts();
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '发送失败', 'error');
    } finally {
      setPrivateBusy(false);
    }
  };

  if (!user) return <><PageHeader title="站内聊天" description="登录并绑定游戏角色后可以私聊和参与群聊。" /><section className="panel"><EmptyBlock title="请先登录" description="游客不能使用站内聊天。" action={<Button variant="primary" onClick={onNeedAuth}><LogIn size={16} /> 登录 / 注册</Button>} /></section></>;
  if (!allowed) return <><PageHeader title="站内聊天" description="登录并绑定游戏角色后可以私聊和参与群聊。" /><section className="panel"><EmptyBlock title="需要绑定游戏角色" description="绑定游戏角色后才能使用聊天功能。" action={<Button variant="primary" onClick={() => { window.location.hash = '/profile'; }}><LockKeyhole size={16} /> 去绑定</Button>} /></section></>;

  const visibleContacts = (contacts || []).filter(contact => !contactSearch.trim() || `${contact.displayName} ${contact.username}`.toLowerCase().includes(contactSearch.trim().toLowerCase()));
  const latestGroupMessage = groupMessages && groupMessages.length > 0 ? groupMessages[groupMessages.length - 1] : null;
  const groupUnread = groupSummary?.unreadCount || 0;
  const privateUnread = (contacts || []).reduce((total, contact) => total + contact.unreadCount, 0);
  const unreadTotal = groupUnread + privateUnread;
  const latestGroupPreview = latestGroupMessage?.content || groupSummary?.lastMessage || '和全服成员一起聊天';
  const latestGroupTime = latestGroupMessage?.createdAt ?? groupSummary?.lastMessageAt ?? null;
  const activeTitle = tab === 'group' ? '全服群聊' : tab === 'oversight' ? '聊天监管台' : selectedPeer?.displayName || '成员私聊';
  const activeSubtitle = tab === 'group' ? '全服成员共同参与的公开频道' : tab === 'oversight' ? '查看全站群聊与私聊记录' : selectedPeer ? `@${selectedPeer.username}` : '从左侧选择一位成员';
  const composerActive = composerFocused || emojiOpen;

  const handleComposerFocus = () => {
    setEmojiOpen(false);
    setComposerFocused(true);
  };

  const handleComposerBlur = () => {
    setComposerFocused(false);
  };

  const toggleEmoji = () => {
    const opening = !emojiOpen;
    setEmojiOpen(opening);
    if (opening) {
      setComposerFocused(false);
      groupInputRef.current?.blur();
      privateInputRef.current?.blur();
    }
  };

  const insertEmoji = (emoji: string) => {
    const isPrivate = tab === 'private';
    const current = isPrivate ? privateDraft : groupDraft;
    const input = isPrivate ? privateInputRef.current : groupInputRef.current;
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? start;
    const next = current.slice(0, start) + emoji + current.slice(end);
    if (next.length > 1000) {
      toast('消息已达到 1000 字上限', 'error');
      return;
    }
    if (isPrivate) setPrivateDraft(next);
    else setGroupDraft(next);
    if (!isMobileLayout) {
      window.requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(start + emoji.length, start + emoji.length);
      });
    }
  };

  const editCustomTitle = async (target: { id: string; username: string; customTitle?: string | null }) => {
    const value = window.prompt('设置自定义头衔（最多 12 个字；留空恢复默认“管理员/成员”头衔）', target.customTitle || '');
    if (value === null) return;
    const title = value.trim();
    if (title.length > 12) return toast('自定义头衔最多 12 个字', 'error');
    try {
      const result = await api<{ user: any }>('/api/admin/users/' + target.id + '/title', { method: 'PATCH', body: JSON.stringify({ title }) });
      setContacts(current => current?.map(contact => contact.id === target.id ? { ...contact, customTitle: result.user.customTitle } : contact) || current);
      setSelectedPeer(current => current?.id === target.id ? { ...current, customTitle: result.user.customTitle } : current);
      toast(title ? '自定义头衔已更新' : '已恢复默认头衔', 'success');
    } catch (error) {
      toast(error instanceof ApiError ? error.message : '头衔设置失败', 'error');
    }
  };

  const openGroup = () => {
    setGroupSummary(current => current ? { ...current, unreadCount: 0 } : current);
    setTab('group');
    setMobileConversationOpen(true);
    window.dispatchEvent(new Event('lspd:chat-unread-updated'));
  };

  const openPrivate = (contact: ChatContact) => {
    setContacts(current => current?.map(item => item.id === contact.id ? { ...item, unreadCount: 0 } : item) || current);
    setPrivateMessages(null);
    setSelectedPeer(contact);
    setTab('private');
    setMobileConversationOpen(true);
  };

  const openOversight = () => {
    setEmojiOpen(false);
    setComposerFocused(false);
    setTab('oversight');
    setMobileConversationOpen(true);
  };

  return (
    <div className={'chat-page' + (mobileConversationOpen ? ' chat-mobile-detail' : '')}>
      <PageHeader title="站内聊天" />
      <section className="chat-app" aria-label="站内聊天">
        <aside className="chat-session-panel">
          <header className="chat-session-panel-head">
            <div>
              <span className="eyebrow">MESSAGES</span>
              <h2>聊天</h2>
            </div>
            <div className="chat-session-head-actions">{permissions.isSuperadmin && <button type="button" className="chat-my-title-button" onClick={() => void editCustomTitle(user)}><Tag size={13} />我的头衔</button>}{unreadTotal > 0 && <span className="chat-total-unread" aria-label={`${unreadTotal} 条未读消息`}>{unreadTotal > 99 ? '99+' : unreadTotal}</span>}</div>
          </header>

          <div className="chat-session-scroll">
            <div className="chat-session-section-label"><span>群聊</span></div>
            <button type="button" className={'chat-session-item' + (tab === 'group' ? ' active' : '')} onClick={openGroup} aria-current={tab === 'group' ? 'page' : undefined}>
              <span className="chat-session-avatar group"><Users size={20} /></span>
              <span className="chat-session-copy">
                <span className="chat-session-title-row"><strong>全服群聊</strong><time>{formatChatListTime(latestGroupTime)}</time></span>
                <span className="chat-session-preview"><span>{latestGroupPreview}</span>{groupUnread > 0 && <span className="chat-session-unread">{groupUnread > 99 ? '99+' : groupUnread}</span>}</span>
              </span>
            </button>

            <div className="chat-session-section-label chat-session-section-toggle-row">
              <button type="button" className="chat-session-section-toggle" aria-expanded={contactsOpen} onClick={() => setContactsOpen(value => !value)}>
                <span>成员私聊</span>
                <span className="chat-session-section-meta">{privateUnread > 0 && <span className="chat-session-unread">{privateUnread > 99 ? '99+' : privateUnread}</span>}<small>{contacts?.length || 0}</small>{contactsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
              </button>
            </div>
            {contactsOpen && <>
              <label className="chat-search">
                <Search size={16} />
                <input value={contactSearch} onChange={event => setContactSearch(event.target.value)} placeholder="搜索成员" aria-label="搜索聊天成员" />
              </label>
              {!contacts ? <div className="chat-session-state"><LoadingBlock label="正在加载成员" /></div> : visibleContacts.length === 0 ? <div className="chat-session-state"><MessageCircle size={20} /><span>{contactSearch ? '没有找到匹配的成员' : '暂无可私聊的成员'}</span></div> : visibleContacts.map(contact => (
                <div className="chat-contact-row" key={contact.id}>
                  <button type="button" className={'chat-session-item' + (tab === 'private' && selectedPeer?.id === contact.id ? ' active' : '')} onClick={() => openPrivate(contact)} aria-current={tab === 'private' && selectedPeer?.id === contact.id ? 'page' : undefined}>
                    <Avatar name={contact.username} avatar={contact.avatar} />
                    <span className="chat-session-copy">
                      <span className="chat-session-title-row"><strong>{contact.displayName}</strong><UserTitleBadge role={contact.role} customTitle={contact.customTitle} compact /><time>{formatChatListTime(contact.lastMessageAt)}</time></span>
                      <span className="chat-session-preview"><span>{contact.lastMessage || `@${contact.username}`}</span>{contact.unreadCount > 0 && <span className="chat-session-unread">{contact.unreadCount > 99 ? '99+' : contact.unreadCount}</span>}</span>
                    </span>
                  </button>
                  {permissions.isSuperadmin && <button type="button" className="chat-contact-title-button" onClick={() => void editCustomTitle(contact)} aria-label={`设置 ${contact.displayName} 的头衔`} title="设置头衔"><Tag size={14} /></button>}
                </div>
              ))}
            </>}

            {permissions.isSuperadmin && <>
              <div className="chat-session-section-label admin"><span>管理</span></div>
              <button type="button" className={'chat-session-item' + (tab === 'oversight' ? ' active' : '')} onClick={openOversight} aria-current={tab === 'oversight' ? 'page' : undefined}>
                <span className="chat-session-avatar oversight"><ShieldCheck size={20} /></span>
                <span className="chat-session-copy">
                  <span className="chat-session-title-row"><strong>聊天监管台</strong></span>
                  <span className="chat-session-preview"><span>查看全站聊天记录</span></span>
                </span>
              </button>
            </>}
          </div>
        </aside>

        <main className="chat-main">
          <header className="chat-main-head">
            <div className="chat-main-identity">
              <button type="button" className="chat-mobile-back" onClick={closeMobileConversation} aria-label="返回会话列表"><ArrowLeft size={21} /></button>
              {tab === 'group' ? <span className="chat-main-avatar group"><Users size={20} /></span> : tab === 'oversight' ? <span className="chat-main-avatar oversight"><ShieldCheck size={20} /></span> : selectedPeer ? <Avatar name={selectedPeer.username} avatar={selectedPeer.avatar} /> : <span className="chat-main-avatar"><MessageCircle size={20} /></span>}
              <div className="chat-main-title"><h2>{activeTitle}</h2><small>{activeSubtitle}</small></div>
            </div>
            {tab === 'oversight' && <Badge tone="warn">仅超级管理员</Badge>}
          </header>

          {tab === 'group' && <>
            <MessageFeed messages={groupMessages} emptyTitle="群里还没有消息" emptyDescription="发送第一条消息，和全服成员打个招呼。" scrollRef={groupScroll} onScroll={() => { const target = groupScroll.current; if (target) groupAtBottom.current = target.scrollHeight - target.scrollTop - target.clientHeight < 60; }} />
            <ChatComposer draft={groupDraft} onDraftChange={value => { setGroupDraft(value); groupAtBottom.current = true; scrollFeedToBottom(groupScroll); }} onSubmit={sendGroup} placeholder="发送群聊消息…" busy={groupBusy} emojiOpen={emojiOpen} onToggleEmoji={toggleEmoji} onCloseEmoji={() => setEmojiOpen(false)} onPickEmoji={insertEmoji} inputRef={groupInputRef} onFocus={handleComposerFocus} onBlur={handleComposerBlur} />
          </>}

          {tab === 'private' && (selectedPeer ? <>
            <MessageFeed messages={privateMessages} emptyTitle="还没有私聊消息" emptyDescription={`给 ${selectedPeer.displayName} 发一条消息开始聊天。`} scrollRef={privateScroll} onScroll={() => { const target = privateScroll.current; if (target) privateAtBottom.current = target.scrollHeight - target.scrollTop - target.clientHeight < 60; }} />
            <ChatComposer draft={privateDraft} onDraftChange={value => { setPrivateDraft(value); privateAtBottom.current = true; scrollFeedToBottom(privateScroll); }} onSubmit={sendPrivate} placeholder={`给 ${selectedPeer.displayName} 发消息…`} busy={privateBusy} emojiOpen={emojiOpen} onToggleEmoji={toggleEmoji} onCloseEmoji={() => setEmojiOpen(false)} onPickEmoji={insertEmoji} inputRef={privateInputRef} onFocus={handleComposerFocus} onBlur={handleComposerBlur} />
          </> : <div className="chat-main-empty"><MessageCircle size={28} /><strong>选择一位成员开始私聊</strong><span>私聊内容仅你和对方可见。</span></div>)}

          {tab === 'oversight' && permissions.isSuperadmin && (
            <div className="chat-oversight-body">
              <div className="chat-oversight-toolbar">
                <div className="chat-filter" role="group" aria-label="监管消息筛选">
                  <button type="button" className={oversightType === 'all' ? 'active' : ''} onClick={() => setOversightType('all')}>全部</button>
                  <button type="button" className={oversightType === 'private' ? 'active' : ''} onClick={() => setOversightType('private')}>私聊</button>
                  <button type="button" className={oversightType === 'group' ? 'active' : ''} onClick={() => setOversightType('group')}>群聊</button>
                </div>
                <span>{oversightMessages?.length || 0} 条记录</span>
              </div>
              <div className="chat-oversight-scroll">
                {!oversightMessages ? <LoadingBlock label="正在加载监管消息" /> : oversightMessages.length === 0 ? <EmptyBlock title="暂无聊天记录" /> : <div className="chat-oversight-list">{oversightMessages.map(message => <article key={message.id} className="chat-oversight-item"><header><div><strong>{message.senderDisplayName}</strong><small>@{message.senderUsername} {message.channelType === 'group' ? '→ 群聊' : '→ ' + (message.recipientDisplayName || message.recipientUsername || '私聊')}</small></div><Badge tone={message.channelType === 'group' ? 'active' : 'warn'}>{message.channelType === 'group' ? '群聊' : '私聊'}</Badge></header><p>{message.content}</p><time>{formatTime(message.createdAt)}</time></article>)}</div>}
              </div>
            </div>
          )}
        </main>
      </section>
    </div>
  );
}



























